/**
 * Explicit-unknown Project Intent semantics.
 *
 * Many intent fields pre-date a first-class epistemic state and therefore use
 * either an enum sentinel (`undecided` / `unknown`) or `undefined` for both:
 *
 *   1. a field the user has never answered, and
 *   2. a field the user deliberately answered "I don't know yet".
 *
 * The stored value must remain unknown in both cases — downstream physics and
 * reports must never treat it as a known fact.  `fieldSource = 'user-unknown'`
 * is the additive provenance marker that preserves the distinction without
 * widening every historical enum or inventing a factual value.
 */
import type { ProjectIntent } from '../../types/siteRag';

export const EXPLICIT_UNKNOWN_INTENT_SOURCE = 'user-unknown' as const;

export type IntentFieldPath = string;

/**
 * Intent fields whose form input was RETIRED (intent-input audit). A legacy
 * persisted intent may still carry a `'user-unknown'` marker for one of these;
 * with no input left to clear it, the marker would otherwise surface forever as
 * a "recorded unknown — follow up" line in prompts, the SED and Path to GO.
 * Retired markers are therefore never reported as explicit unknowns.
 */
const RETIRED_INTENT_FIELDS: ReadonlySet<IntentFieldPath> = new Set([
  'offtake.priceBasis',
  'landControl',
]);

function sourceRecord(
  intent: ProjectIntent,
): Record<string, string | undefined> {
  return (intent.fieldSource ?? {}) as Record<string, string | undefined>;
}

/** True only when the user explicitly recorded this field as unknown. */
export function isIntentFieldExplicitlyUnknown(
  intent: ProjectIntent | null | undefined,
  field: IntentFieldPath,
): boolean {
  if (!intent) return false;
  if (RETIRED_INTENT_FIELDS.has(field)) return false;
  if (sourceRecord(intent)[field] === EXPLICIT_UNKNOWN_INTENT_SOURCE) return true;
  if (field === 'bopHeatRejection' && intent.bopHeatRejection === 'unknown') return true;
  return false;
}

/**
 * Stamp an already-built unknown-value intent patch. The caller remains
 * responsible for writing the field's canonical unknown representation
 * (`undecided`, `unknown`, or `undefined`).
 */
export function markIntentFieldExplicitlyUnknown(
  next: ProjectIntent,
  field: IntentFieldPath,
): ProjectIntent {
  const fieldSource = { ...sourceRecord(next) };
  fieldSource[field] = EXPLICIT_UNKNOWN_INTENT_SOURCE;
  return {
    ...next,
    fieldSource: fieldSource as ProjectIntent['fieldSource'],
  };
}

/**
 * Remove only the explicit-unknown marker after a concrete value is chosen.
 * Other provenance (accepted suggestion, document inference, manual stamp)
 * is preserved byte-for-byte.
 */
export function clearIntentFieldExplicitlyUnknown(
  next: ProjectIntent,
  field: IntentFieldPath,
): ProjectIntent {
  const current = sourceRecord(next);
  if (current[field] !== EXPLICIT_UNKNOWN_INTENT_SOURCE) return next;
  const fieldSource = { ...current };
  delete fieldSource[field];
  return {
    ...next,
    fieldSource: (Object.keys(fieldSource).length > 0
      ? fieldSource
      : undefined) as ProjectIntent['fieldSource'],
  };
}

type IntentPhase = NonNullable<ProjectIntent['phases']>[number];

/** The phase a `phases.<id>.<field>` path belongs to, with the field
 * remainder; null for a non-phase path. `phase` is undefined when the id no
 * longer names a phase of this intent. */
function phaseScopedField(
  intent: ProjectIntent | null | undefined,
  field: IntentFieldPath,
): { phase: IntentPhase | undefined; leaf: string } | null {
  if (!field.startsWith('phases.')) return null;
  const phase = (intent?.phases ?? []).find(candidate =>
    typeof candidate?.id === 'string' && candidate.id.length > 0 && field.startsWith(`phases.${candidate.id}.`));
  if (phase) return { phase, leaf: field.slice(`phases.${phase.id}.`.length) };
  return { phase: undefined, leaf: field.split('.').slice(2).join('.') || field };
}

const PHASE_EXPORT_MODE_FIELD = 'powerSourceConfig.exportMode';

/**
 * A per-phase marker whose control no longer exists is orphaned: the phase
 * was removed (or phasing turned off), or an export-mode marker sits on a
 * phase that is not grid-connected (the export control is hidden there). With
 * no input left to clear it, it is never reported, like a retired field.
 */
function isOrphanedPhaseMarker(intent: ProjectIntent, field: IntentFieldPath): boolean {
  const scoped = phaseScopedField(intent, field);
  if (!scoped) return false;
  if (!scoped.phase) return true;
  return scoped.leaf === PHASE_EXPORT_MODE_FIELD
    && scoped.phase.powerSourceConfig?.gridConnection !== 'grid-connected';
}

/** Stable list used by report prompt builders and review surfaces. */
export function explicitUnknownIntentFields(
  intent: ProjectIntent | null | undefined,
): string[] {
  if (!intent) return [];
  const fields = new Set(
    Object.entries(sourceRecord(intent))
      .filter(([field, source]) =>
        source === EXPLICIT_UNKNOWN_INTENT_SOURCE && !RETIRED_INTENT_FIELDS.has(field)
        && !isOrphanedPhaseMarker(intent, field))
      .map(([field]) => field),
  );
  if (intent.bopHeatRejection === 'unknown') {
    fields.add('bopHeatRejection');
  }
  return Array.from(fields).sort((a, b) => a.localeCompare(b));
}

const FIELD_LABEL: Readonly<Record<string, string>> = {
  workloadProfile: 'Workload profile',
  powerRequirement: 'Power requirement',
  coolingStrategy: 'IT cooling strategy',
  nuclearInterest: 'Nuclear interest',
  priority: 'Project priority',
  timeline: 'Target in-service timeline',
  reactorDesignClass: 'Reactor design class',
  pueValue: 'PUE',
  powerFactor: 'Power factor',
  annualDemandUtilizationRangePct: 'Annual data-center demand utilization / load factor',
  bopHeatRejection: 'BoP heat rejection',
  coolingGeneration: 'Chiller plant',
  interconnectionStatus: 'Utility service / interconnection status',
  waterSource: 'Water source',
  fuelAccess: 'Fuel / gas access',
  offtake: 'Offtake / PPA structure',
  capacityCreditIntent: 'Firm-capacity target',
  carbonCommitment: 'Carbon commitment',
  electricitySupply: 'Electricity supply / sale arrangement',
};

const FIELD_RECOMMENDATION: Readonly<Record<string, string>> = {
  workloadProfile: 'Confirm the primary workload with the operator before fixing rack density and cooling assumptions.',
  powerRequirement: 'Obtain a phase load schedule or approve a documented screening range before sizing infrastructure.',
  coolingStrategy: 'Have the design team select the rack-side cooling basis; compare air, liquid, and hybrid cases meanwhile.',
  nuclearInterest: 'Confirm the sponsor\'s energy strategy before making nuclear-specific licensing or schedule claims.',
  priority: 'Name the primary trade-off—cost, speed, sustainability, or resilience—before final scoring.',
  timeline: 'Set a target COD or approved planning window and test the critical path against it.',
  reactorDesignClass: 'Shortlist a reactor class with the owner and vendor before using class-specific licensing or fuel assumptions.',
  pueValue: 'Obtain the design PUE target or document a clearly labelled screening assumption.',
  powerFactor: 'Confirm the electrical design power factor before fixing transformer and switchgear MVA.',
  annualDemandUtilizationRangePct: 'Confirm a low/high annual demand-utilization range with the operator before tightening annual water and energy estimates.',
  bopHeatRejection: 'Shortlist dry, hybrid, and wet rejection against local design conditions, then obtain a vendor capacity check.',
  coolingGeneration: 'Confirm the chiller-plant technology and its heat source before crediting PUE or waste-heat benefits.',
  interconnectionStatus: 'Record the utility load-service milestone or applicable generator queue status from its own agreement or application; keep delivery and commissioning dates separate.',
  waterSource: 'Identify a candidate source and verify capacity, rights, treatment, and drought constraints.',
  fuelAccess: 'Screen the nearest viable supply path and obtain a firm-deliverability statement.',
  offtake: 'Confirm whether the commercial basis is merchant, contracted, or hybrid before relying on revenue assumptions.',
  capacityCreditIntent: 'Confirm the market accreditation target, or explicitly record that no firm-capacity credit is sought.',
  carbonCommitment: 'Confirm the governing corporate or project carbon policy before constraining technology options.',
  electricitySupply: 'Confirm whether the operator generates for its own use, buys on-site power from a separate company, or takes utility supply; third-party on-site sales depend on state law and utility service territory.',
};

/** Per-phase field labels (lower case; prefixed with the phase name). */
const PHASE_FIELD_LABEL: Readonly<Record<string, string>> = {
  generationDuty: 'on-site generation duty',
  [PHASE_EXPORT_MODE_FIELD]: 'grid export mode',
};

const PHASE_FIELD_RECOMMENDATION: Readonly<Record<string, string>> = {
  generationDuty: 'Confirm the on-site generation operating duty (emergency/standby, peaking, prime/continuous or mixed) before scoping air permits; emergency approval is not prime-power permission.',
  [PHASE_EXPORT_MODE_FIELD]: 'Confirm whether this grid-connected phase will export power or run grid-parallel without export; zero export does not by itself remove utility interconnection review.',
};

function humanisedLeaf(field: string): string {
  const leaf = field.split('.').at(-1) ?? field;
  return leaf
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .replace(/^./, (char) => char.toUpperCase());
}

/**
 * Human-readable label for an explicit-unknown field path. A per-phase path
 * (`phases.<id>.…`) names its phase ("Phase 2 grid export mode"); pass the
 * intent so the phase's own label or sequence can be used.
 */
export function explicitUnknownIntentFieldLabel(field: string, intent?: ProjectIntent | null): string {
  const known = FIELD_LABEL[field];
  if (known) return known;
  const scoped = phaseScopedField(intent, field);
  if (scoped) {
    const name = scoped.phase
      ? (typeof scoped.phase.label === 'string' && scoped.phase.label.trim()) || `Phase ${scoped.phase.sequence}`
      : 'Project phase';
    const leafLabel = PHASE_FIELD_LABEL[scoped.leaf] ?? humanisedLeaf(scoped.leaf).replace(/^./, (char) => char.toLowerCase());
    return `${name} ${leafLabel}`;
  }
  return humanisedLeaf(field);
}

/** Actionable follow-up; never fills or guesses the unknown fact. */
export function explicitUnknownIntentRecommendation(field: string): string {
  const scoped = phaseScopedField(null, field);
  return FIELD_RECOMMENDATION[field]
    ?? (scoped ? PHASE_FIELD_RECOMMENDATION[scoped.leaf] : undefined)
    ?? `Confirm ${(FIELD_LABEL[field] ?? humanisedLeaf(field)).toLowerCase()} with the accountable project owner and update Project Intent.`;
}
